import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Star, Pencil, PaintBucket, Crown, Heart, Truck, ShieldCheck,
  Calculator, BookOpen, Microscope, Globe2, Brain, Trophy, Music,
  Bus, Smile, Library, Dog, GraduationCap, BookMarked,
  Frame, Sticker, DoorOpen, Hand, Megaphone,
  Package, Award, ClipboardList, Cake } from "lucide-react";
import { LOGO_URL, api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";

const Doodle = ({ children, className = "", style = {} }) => (
  <div className={`absolute pointer-events-none ${className}`} style={style}>{children}</div>
);

// Known icon/color meta for subjects and grades (new entries get fallback)
const SUBJECT_META = {
  "Mathematics":        { color: "#FF1F87", icon: <Calculator className="w-7 h-7" strokeWidth={2.5}/> },
  "Language Arts":      { color: "#FFC500", icon: <BookOpen className="w-7 h-7" strokeWidth={2.5}/> },
  "Science":            { color: "#00E0AA", icon: <Microscope className="w-7 h-7" strokeWidth={2.5}/> },
  "Social Studies":     { color: "#00A6E0", icon: <Globe2 className="w-7 h-7" strokeWidth={2.5}/> },
  "Mental Ability":     { color: "#B845FF", icon: <Brain className="w-7 h-7" strokeWidth={2.5}/> },
  "P.E.":               { color: "#FF6B1A", icon: <Trophy className="w-7 h-7" strokeWidth={2.5}/> },
  "Music":              { color: "#00A6E0", icon: <Music className="w-7 h-7" strokeWidth={2.5}/> },
  "Integrated Studies": { color: "#4CAF50", icon: <BookMarked className="w-7 h-7" strokeWidth={2.5}/> },
};
const GRADE_META = {
  "Kindergarten": { color: "#FFC500", icon: <Smile className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 1":      { color: "#FF1F87", icon: <Bus className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 2":      { color: "#00E0AA", icon: <Library className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 3":      { color: "#00A6E0", icon: <Dog className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 4":      { color: "#B845FF", icon: <Star className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 5":      { color: "#FF6B1A", icon: <Crown className="w-7 h-7" strokeWidth={2.5}/> },
  "Grade 6":      { color: "#00A6E0", icon: <BookMarked className="w-7 h-7" strokeWidth={2.5}/> },
};
const FALLBACK_COLORS = ["#FF1F87","#FFC500","#00E0AA","#00A6E0","#B845FF","#FF6B1A","#4CAF50"];
const FALLBACK_ICONS  = [
  <Sparkles className="w-7 h-7" strokeWidth={2.5}/>,
  <Star className="w-7 h-7" strokeWidth={2.5}/>,
  <BookOpen className="w-7 h-7" strokeWidth={2.5}/>,
  <GraduationCap className="w-7 h-7" strokeWidth={2.5}/>,
  <Heart className="w-7 h-7" strokeWidth={2.5}/>,
  <Microscope className="w-7 h-7" strokeWidth={2.5}/>,
  <Globe2 className="w-7 h-7" strokeWidth={2.5}/>,
];
const buildItems = (names, metaMap) =>
  names.map((name, i) => ({
    name,
    ...(metaMap[name] || {
      color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      icon: FALLBACK_ICONS[i % FALLBACK_ICONS.length],
    }),
  }));

// Static fallbacks used while catalog loads
const SUBJECTS_DEFAULT = buildItems(Object.keys(SUBJECT_META), SUBJECT_META);
const GRADES_DEFAULT   = buildItems(Object.keys(GRADE_META), GRADE_META);
const DECOR = [
  { name: "Borders", color: "#FFC500", icon: <Frame className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Accents", color: "#FF1F87", icon: <Sticker className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Door Sets", color: "#00E0AA", icon: <DoorOpen className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Welcome Signs", color: "#00A6E0", icon: <Hand className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Motivational Charts & Banners", color: "#B845FF", icon: <Megaphone className="w-7 h-7" strokeWidth={2.5}/> },
];
const ESSENTIALS = [
  { name: "Storage Bins", color: "#00A6E0", icon: <Package className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Stickers", color: "#FFC500", icon: <Star className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Reward Charts & Buttons", color: "#FF1F87", icon: <Award className="w-7 h-7" strokeWidth={2.5}/> },
  { name: "Reward Certificates", color: "#00E0AA", icon: <ClipboardList className="w-7 h-7" strokeWidth={2.5}/> },
];

const CardItem = ({ item, kind, sectionParam, paramName, imgFn }) => {
  const params = new URLSearchParams();
  if (sectionParam) params.set("section", sectionParam);
  params.set(paramName, item.name);
  const image = imgFn(`${kind}:${item.name}`);
  return (
    <Link to={`/shop?${params.toString()}`}
      data-testid={`cat-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className="c4t-card !p-3 flex flex-col items-stretch min-h-[170px]"
      style={{ background: item.color + "1f" }}>
      <div className="aspect-square w-full overflow-hidden rounded-2xl border-2 border-black bg-white flex items-center justify-center">
        {image ? (
          <img src={image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <span className="rounded-full p-4 border-[3px] border-black" style={{ background: item.color, color: ["#FFC500"].includes(item.color) ? "#000" : "#fff" }}>
            {item.icon}
          </span>
        )}
      </div>
      <h3 className="mt-3 text-base font-['Fredoka'] font-bold leading-tight text-center">{item.name}</h3>
    </Link>
  );
};

const SectionRow = ({ title, subtitle, items, sectionParam, paramName = "category", kind, testid, imgFn, color = "#FF1F87" }) => (
  <section className="max-w-7xl mx-auto px-6 py-10" data-testid={testid}>
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold" style={{ color }}>{title}</h2>
        {subtitle && <p className="c4t-desc text-base sm:text-lg mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {items.map(item => (
        <CardItem key={item.name} item={item} kind={kind} sectionParam={sectionParam} paramName={paramName} imgFn={imgFn} />
      ))}
    </div>
  </section>
);

export default function Home() {
  const { t, img } = useSettings();
  const { format } = useCurrency();
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [shopperPhotos, setShopperPhotos] = useState([]);
  useEffect(() => {
    document.title = t("hero.title", "Cheap4Teach – Affordable Classroom Essentials for Caribbean Teachers");
    api.get("/products", { params: { featured: true } }).then(r => setFeatured(Array.isArray(r.data) ? r.data.slice(0, 8) : [])).catch(() => setFeatured([]));
    api.get("/reviews").then(r => setReviews(r.data.slice(0, 5))).catch(() => {});
    api.get("/shopper-photos").then(r => setShopperPhotos(r.data)).catch(() => {});
  }, [t]);

  const firstName = (user?.name || "").split(" ")[0];
  const heroEyebrow = user && firstName
    ? `Hello, ${firstName}!`
    : t("hero.eyebrow", "Hello, Amazing Teacher!");
  const heroTitle = t("hero.title", "Affordable Classroom Essentials for Caribbean Teachers");

  // ── Dynamic catalog ──────────────────────────────────────
  const [catalog, setCatalog] = React.useState(null);
  React.useEffect(() => {
    api.get("/catalog").then(r => setCatalog(r.data)).catch(() => {});
  }, []);

  const DECOR_META = {
    "Borders": { color: "#FFC500", icon: <Frame className="w-7 h-7" strokeWidth={2.5}/> },
    "Accents": { color: "#FF1F87", icon: <Sticker className="w-7 h-7" strokeWidth={2.5}/> },
    "Door Sets": { color: "#00E0AA", icon: <DoorOpen className="w-7 h-7" strokeWidth={2.5}/> },
    "Welcome Signs": { color: "#00A6E0", icon: <Hand className="w-7 h-7" strokeWidth={2.5}/> },
    "Motivational Charts & Banners": { color: "#B845FF", icon: <Megaphone className="w-7 h-7" strokeWidth={2.5}/> },
  };
  const ESSENTIALS_META = {
    "Storage Bins": { color: "#00A6E0", icon: <Package className="w-7 h-7" strokeWidth={2.5}/> },
    "Stickers": { color: "#FFC500", icon: <Star className="w-7 h-7" strokeWidth={2.5}/> },
    "Reward Charts & Buttons": { color: "#FF1F87", icon: <Award className="w-7 h-7" strokeWidth={2.5}/> },
    "Reward Certificates": { color: "#00E0AA", icon: <ClipboardList className="w-7 h-7" strokeWidth={2.5}/> },
  };

  const subjects   = buildItems(catalog?.sections?.["Charts by Subject"]   || Object.keys(SUBJECT_META), SUBJECT_META);
  const grades     = buildItems(catalog?.grades                             || Object.keys(GRADE_META),   GRADE_META);
  const decor      = buildItems(catalog?.sections?.["Class Decor"]          || Object.keys(DECOR_META),   DECOR_META);
  const essentials = buildItems(catalog?.sections?.["Teacher Essentials"]   || Object.keys(ESSENTIALS_META), ESSENTIALS_META);
  // ─────────────────────────────────────────────────────────

  return (
    <div>
      <section className="relative overflow-hidden bg-white" data-testid="home-hero">
        <Doodle className="top-10 left-6 float-slow"><Star className="w-10 h-10 text-[#FFC500] fill-[#FFC500]" /></Doodle>
        <Doodle className="top-32 right-10 float-slow" style={{ animationDelay: "1s" }}><Pencil className="w-12 h-12 text-[#FF1F87]" strokeWidth={2.5} /></Doodle>
        <Doodle className="bottom-20 left-20 float-slow" style={{ animationDelay: "2s" }}><Crown className="w-10 h-10 text-[#00E0AA]" strokeWidth={2.5} /></Doodle>
        <Doodle className="bottom-10 right-28 float-slow" style={{ animationDelay: "1.5s" }}><PaintBucket className="w-12 h-12 text-[#00A6E0]" strokeWidth={2.5} /></Doodle>
        <Doodle className="top-1/2 left-1/3 float-slow opacity-50"><Sparkles className="w-8 h-8 text-[#B845FF]" /></Doodle>

        <div className="relative max-w-7xl mx-auto px-6 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="pop-in">
            <span className="c4t-badge" data-testid="hero-badge">{heroEyebrow}</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-['Fredoka'] font-bold leading-tight" style={{ color: "#B845FF" }}>
              {heroTitle}
            </h1>
            <p className="mt-5 text-lg sm:text-xl c4t-desc max-w-xl">
              {t("hero.subtitle", "Made fun for Caribbean schools. Shop charts, decor, and supplies for every grade & subject.")}
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/shop" className="c4t-btn c4t-btn-pink" data-testid="hero-shop-now-btn">{t("hero.cta_primary", "Shop Now")}</Link>
              <Link to="/shipping" className="c4t-btn c4t-btn-yellow" data-testid="hero-shipping-info-btn">{t("hero.cta_secondary", "View Shipping Info")}</Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-6 text-sm c4t-desc">
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#00E0AA]" strokeWidth={2.5} /> {t("hero.feature_1", "Shipping Available")}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00A6E0]" strokeWidth={2.5} /> {t("hero.feature_2", "Secure Checkout")}</span>
              <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-[#FF1F87] fill-[#FF1F87]" /> {t("hero.feature_3", "Teacher Owned")}</span>
            </div>
          </div>

          <div className="relative pop-in" style={{ animationDelay: "0.15s" }}>
            <div className="relative mx-auto max-w-md aspect-square rounded-[3rem] overflow-hidden">
              <img
                src={t("hero.image", "https://customer-assets.emergentagent.com/job_teach-supplies-shop/artifacts/9l7cfi2w_PHOTO-2026-05-16-16-26-38.jpg")}
                alt="Cheap4Teach Jamaica – teacher in a colorful classroom with happy students"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <span className="absolute -top-4 -left-4 c4t-badge !bg-[#00E0AA]">{t("hero.badge_left", "Bright!")}</span>
            <span className="absolute -bottom-3 -right-3 c4t-badge !bg-[#00A6E0] !text-white" style={{ transform: "rotate(6deg)" }}>{t("hero.badge_right", "Fun!")}</span>
          </div>
        </div>
      </section>

      <SectionRow title={t("section.charts.title", "Charts by Subject")} subtitle={t("section.charts.subtitle")}
        items={subjects} sectionParam="Charts by Subject" kind="subject" testid="section-charts-by-subject" imgFn={img} color="#FF1F87" />

      <SectionRow title={t("section.grade.title", "Shop by Grade")} subtitle={t("section.grade.subtitle")}
        items={grades} paramName="grade" kind="grade" testid="section-shop-by-grade" imgFn={img} color="#00A6E0" />

      <SectionRow title={t("section.decor.title", "Class Decor")} subtitle={t("section.decor.subtitle")}
        items={decor} sectionParam="Class Decor" kind="decor" testid="section-class-decor" imgFn={img} color="#B845FF" />

      <SectionRow title={t("section.essentials.title", "Teacher Essentials")} subtitle={t("section.essentials.subtitle")}
        items={essentials} sectionParam="Teacher Essentials" kind="essentials" testid="section-teacher-essentials" imgFn={img} color="#00E0AA" />

      <section className="max-w-7xl mx-auto px-6 py-10" data-testid="section-graduation">
        <div className="mb-6">
          <h2 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold" style={{ color: "#FF6B1A" }}>{t("section.graduation.title", "Graduation & Teacher Appreciation")}</h2>
          <p className="c4t-desc text-base sm:text-lg mt-1">{t("section.graduation.subtitle", "Celebrate big moments.")}</p>
        </div>
        <Link to="/shop?section=Graduation+%26+Teacher+Appreciation" className="block">
          <div className="c4t-card !p-10 text-center !bg-white flex flex-col items-center gap-3" data-testid="graduation-card">
            <div className="flex gap-3">
              <span className="rounded-full p-3 border-[3px] border-black bg-[#B845FF] text-white"><GraduationCap className="w-7 h-7" strokeWidth={2.5}/></span>
              <span className="rounded-full p-3 border-[3px] border-black bg-[#FF1F87] text-white"><Cake className="w-7 h-7" strokeWidth={2.5}/></span>
              <span className="rounded-full p-3 border-[3px] border-black bg-[#FFC500]"><Heart className="w-7 h-7" strokeWidth={2.5}/></span>
            </div>
            <h3 className="text-xl font-['Fredoka'] font-bold mt-3">{t("section.graduation.empty_title", "Nothing added yet")}</h3>
            <p className="c4t-desc">{t("section.graduation.empty_subtitle", "Check back soon for graduation kits, teacher appreciation gifts, and more.")}</p>
          </div>
        </Link>
      </section>

      {Array.isArray(featured) && featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-14" data-testid="home-featured">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold" style={{ color: "#FFC500" }}>{t("section.featured.title", "Teacher Favourites")}</h2>
              <p className="c4t-desc text-base sm:text-lg mt-1">{t("section.featured.subtitle", "Loved by classrooms everywhere.")}</p>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex c4t-btn c4t-btn-white text-sm">{t("section.featured.see_all", "See All")}</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="c4t-card !p-3" data-testid={`featured-product-${p.id}`}>
                <div className="aspect-square overflow-hidden rounded-2xl bg-white border-2 border-black/20">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="mt-3 font-['Fredoka'] font-bold text-lg">{p.name}</h3>
                <p className="text-[#FF1F87] font-bold text-xl">{format(p.price, p.currency)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Real Shoppers Marquee ── */}
      {shopperPhotos.length > 0 && (
        <section className="py-10 overflow-hidden bg-[#FFF9F2] border-y-[3px] border-black">
          <div className="max-w-7xl mx-auto px-6 mb-5">
            <h2 className="text-2xl sm:text-3xl font-['Fredoka'] font-bold text-center">
              Real Shoppers
            </h2>
            <p className="c4t-desc text-center mt-1">Our community rocking their Cheap4Teach purchases!</p>
          </div>
          <style>{`
            @keyframes marquee-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-track {
              display: flex;
              width: max-content;
              animation: marquee-scroll 30s linear infinite;
            }
            .marquee-track:hover { animation-play-state: paused; }
          `}</style>
          <div className="relative">
            <div className="marquee-track gap-4" style={{ paddingLeft: "1rem" }}>
              {[...shopperPhotos, ...shopperPhotos].map((p, i) => (
                <div
                  key={i}
                  className="shrink-0 w-44 h-44 sm:w-52 sm:h-52 rounded-2xl overflow-hidden border-[3px] border-black shadow-[4px_4px_0_#FF1F87]"
                  style={{ marginRight: "1rem" }}
                >
                  <img src={p.image} alt="Happy shopper" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="rounded-[2.5rem] bg-black border-[3px] border-black shadow-[8px_8px_0_#FF1F87] p-10 text-white text-center relative overflow-hidden">
          <Sparkles className="absolute top-4 left-6 w-8 h-8 text-[#FFC500]" />
          <Star className="absolute bottom-4 right-8 w-8 h-8 text-[#00E0AA] fill-[#00E0AA]" />
          <h2 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold text-white">{t("banner.title", "Brighten Your Classroom Today")}</h2>
          <p className="mt-3 text-white/90 text-lg font-semibold">{t("banner.subtitle", "Affordable essentials made for Caribbean primary school classrooms.")}</p>
          <Link to="/shop" className="mt-6 inline-flex c4t-btn c4t-btn-yellow" data-testid="banner-shop-btn">{t("banner.cta", "Start Shopping")}</Link>
        </div>
      </section>

      {/* ── Wall of Love ── */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-14">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold">
              Wall of Love
            </h2>
            <p className="c4t-desc text-lg mt-2">What teachers are saying about Cheap4Teach</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {reviews.map(r => (
              <div key={r.id} className="c4t-card flex flex-col gap-3 relative">
                {/* Stars */}
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} className="w-4 h-4" viewBox="0 0 20 20" fill={s <= r.rating ? "#FFC500" : "#e5e7eb"}>
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                {/* Comment */}
                <p className="text-sm c4t-desc flex-1 italic">&ldquo;{r.text}&rdquo;</p>
                {/* Reviewer */}
                <div className="border-t border-black/10 pt-2 mt-auto">
                  <p className="font-bold text-sm">{r.reviewer_name}</p>
                  <p className="text-xs c4t-desc">{new Date(r.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
